import { useMemo, useState } from 'react';

import { cloneDeep } from 'lodash';
import toast from 'react-hot-toast';

import { AutoFields, AutoForm, SubmitField, useForm } from '@bitstopco/bitstop-theme';
import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { Box, Button, DialogActions, DialogContent, Divider, Typography } from '@mui/material';

import { UsersRoleColumnV2 } from '@/components/users/tableColumns/UsersRoleColumnV2';

import {
  useGetNonceRolesQuery,
  usePutEditUserRoleV2Mutation,
} from '@/store/api/services/users/users';

import SCHEMAS from '@/forms/schemas';

import type { EditUserRoleModel } from '@/types';

const UserEditRoleV2 = ({ hide, props }: ModalChildrenProps) => {
  const user = useState(props)[0];

  const { data = [] } = useGetNonceRolesQuery();
  const [editRole] = usePutEditUserRoleV2Mutation();

  const baseSchema = useMemo(() => {
    const schema = cloneDeep(SCHEMAS.EDIT_USER_ROLE);
    schema.properties.role.uniforms.options = data
      .filter(({ id }) => id !== user?.role_id)
      .map(({ id, name }) => ({
        key: id,
        value: id,
        label: name,
      }));

    return schema;
  }, [data, user]);

  const { schema } = useForm(baseSchema);

  const handleSubmit = async ({ role }: EditUserRoleModel) => {
    try {
      await editRole({
        id: user.id,
        role_id: role,
      }).unwrap();

      toast.success('Role updated successfully');
      hide();
    } catch {}
  };

  return (
    <AutoForm<EditUserRoleModel> showInlineError schema={schema} onSubmit={handleSubmit}>
      <DialogContent sx={{ my: 2 }} data-test="edit-user-role-v2-modal">
        <Typography variant="h6">Current Role</Typography>
        <UsersRoleColumnV2 value={user?.role_id} />

        <Box mt={3}>
          <AutoFields />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={hide}>Cancel</Button>
        <SubmitField data-test="edit-user-role-v2-modal-submit-btn">Edit Role</SubmitField>
      </DialogActions>
    </AutoForm>
  );
};

export default UserEditRoleV2;
